import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import '../../styles/Widgets.css';

export interface WidgetDocsModalProps {
  title: string;
  /** Public URL to fetch — see widgetSdkLinks.ts (WIDGET_DOCS_URL / WIDGET_INSTALL_DOCS_URL). */
  url: string;
  onClose: () => void;
}

/** Renders one of docs/widgets/{README,INSTALLING}.md in-app, so "how do I connect this" has
 *  somewhere to actually be read instead of only existing as a file in the repo. Same trust
 *  level as WidgetListing's README rendering: no raw HTML, no remote images, links open safely —
 *  these are Momentum AMP's own docs, but there's no reason to treat them any less carefully. */
const WidgetDocsModal: React.FC<WidgetDocsModalProps> = ({ title, url, onClose }) => {
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setText(null);
    setError(null);
    fetch(url, { signal: controller.signal })
      .then(res => { if (!res.ok) throw new Error(`Couldn't load this guide (${res.status})`); return res.text(); })
      .then(t => { if (!controller.signal.aborted) setText(t); })
      .catch(err => { if (!controller.signal.aborted) setError(err instanceof Error ? err.message : 'Failed to load'); });
    return () => controller.abort();
  }, [url]);

  return (
    <div className="mt-modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="mt-modal" style={{ maxWidth: 720, maxHeight: '82vh', display: 'flex', flexDirection: 'column' }}>
        <div className="mt-modal__header">
          <div className="mt-modal__header-info">
            <h3 className="mt-modal__title">{title}</h3>
          </div>
          <button type="button" className="mt-modal__close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div style={{ overflowY: 'auto', paddingRight: 4 }}>
          {error && <p className="wg-listing__muted">{error}</p>}
          {text === null && !error && <p className="wg-listing__muted">Loading…</p>}
          {text !== null && (
            <div className="wg-readme">
              <ReactMarkdown components={{
                a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>,
                img: () => null,
              }}>
                {text}
              </ReactMarkdown>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WidgetDocsModal;
